import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { BaseChildState } from '@stateMachine/BaseChildState'

/**
 * Server reported the round hit the win cap (current_round.misc_prizes.capamount_reached).
 *
 * Registered last in FinalState so it runs after the free spins / Hold & Win end popups,
 * and holds the round open until the player dismisses it.
 */
export class MaxExposureState extends BaseChildState {
    /**
     * Safety net, same pattern as FreeSpinsIntroState / FreeSpinsSummaryState: this state
     * parks the round on maxExposureComplete, so if the popup component never answers
     * (missing asset, component not built) the round would never close.
     */
    private static readonly COMPLETE_FALLBACK_MS: number = 8000

    private _fallbackTimeoutId: number | null = null
    private _isCompleted: boolean = false

    public canTrigger(): boolean {
        if (this._model.isMaxExposureShown) {
            return false
        }

        return Boolean(this._model.spinData?.cap_amount_reached)
    }

    public begin() {
        // Set before the emit: the popup can answer synchronously (missing spine, or already
        // playing), which would otherwise end this state before the guard was in place.
        this._model.isMaxExposureShown = true
        this._isCompleted = false

        // FreeSpinsSummaryState emits unLockGamePanel just before this state runs, so the
        // panel is live during the hand-off. Block it until the popup is dismissed.
        Facade.isPopupBlockingInput = true

        ;(GameSignals as any).maxExposureComplete?.connect(this.onComplete)

        // Reaching the cap must not roll straight into another auto spin.
        GameSignals.stopAutoplay.emit()
        ;(GameSignals as any).showMaxExposure?.emit()

        this._fallbackTimeoutId = window.setTimeout(this.onComplete, MaxExposureState.COMPLETE_FALLBACK_MS)
    }

    private onComplete = () => {
        if (this._isCompleted) {
            return
        }

        this._isCompleted = true
        this.clearFallback()
        Facade.isPopupBlockingInput = false
        this.end()
    }

    private clearFallback(): void {
        if (this._fallbackTimeoutId !== null) {
            window.clearTimeout(this._fallbackTimeoutId)
            this._fallbackTimeoutId = null
        }
    }

    public cleanUp() {
        this.clearFallback()
        this._isCompleted = false
        Facade.isPopupBlockingInput = false
        ;(GameSignals as any).maxExposureComplete?.disconnect(this.onComplete)
    }
}
